import { Button } from "@Components/Control/Button";
import { GqlErrorDisplayer } from '@Components/Feedback/Modal/ErrorModal';
import { Flex } from "@Components/Layout/Flex";
import PageHeader from '@Components/Layout/PageHeader';
import Title from "@Components/Utilities/Title";
import LayoutContainer from '@Containers/Layout/LayoutContainer';
import { ClientError } from 'graphql-request';
import React, { ReactNode, useEffect, useState } from 'react';
import { ErrorResponse, useRouteError } from "react-router";
import StackTrace, { StackFrame } from 'stacktrace-js';

export function AppErrorHandler({
    error,
    resetErrorBoundary
}: {
    error: unknown,
    resetErrorBoundary: () => void,
}) {
    return (
        <ErrorHandler {...{
            error,
            resetErrorBoundary,
            level: 'app',
        }} />
    )
}

type RouteError = Error | ErrorResponse | TypeError;

export function RouteErrorHandler() {
    const error = useRouteError() as RouteError;

    if (
        error instanceof Error
        || error instanceof TypeError
    ) {
        return (
            <LayoutContainer>
                <ErrorDetails error={error} />
            </LayoutContainer>
        );
    } else if (error) {
        return (
            <LayoutContainer>
                <PageHeader title={error.status + ''} />
                <h2>{error.data?.message ?? 'Something went wrong'}</h2>
            </LayoutContainer>
        );
    }
}

export default function ErrorHandler({
    error,
    resetErrorBoundary,
    level = 'content',
}: {
    error: unknown,
    resetErrorBoundary: () => void,
    level?: 'app' | 'content',
}) {
    return (
        <ErrorDetails
            error={error}
            ResetButton={<Button
                onClick={resetErrorBoundary}
                size="lg"
            >
                Reset
            </Button>}
        />
    );
}

type AppError =
    | Error
    | TypeError
    | (Error & { request?: unknown; response?: unknown });

function isAppError(error: unknown): error is AppError {
    return error instanceof Error;
}

declare global {
    interface TypeError {
        fileName?: string;
        lineNumber?: number;
        columnNumber?: number;
        stack?: string;
    }
}

function ErrorDetails({
    error,
    ResetButton = null
}: {
    error: unknown,
    ResetButton?: ReactNode
}) {
    if (!isAppError(error)) {
        console.dir(error);
        return null;
    }

    if (error instanceof ClientError) {
        return (
            <>
                <Title value="GraphQL Error" />

                <PageHeader title="GraphQL Error" />

                <GqlErrorDisplayer response={error.response} />
            </>
        );
    }

    if (error instanceof TypeError) {
        return (
            <>
                <Title value={error.name} />

                <Flex className="justify-between">
                    <h1 className="text-3xl font-bold">{error.name}</h1>

                    <div>
                        {ResetButton}
                    </div>
                </Flex>

                <pre className="bg-gray-100 p-3 mt-2">
                        {error.message}
                    </pre>

                <p className="mt-2">
                    {error.fileName && (
                        <><strong>File:</strong> {error.fileName}<br /></>
                    )}

                    {error.lineNumber && (
                        <><strong>Line:</strong> {error.lineNumber}<br /></>
                    )}

                    {error.columnNumber && (
                        <><strong>Column:</strong> {error.columnNumber}</>
                    )}
                </p>

                <h2 className="mt-3 text-xl font-bold">Trace</h2>

                <ErrorDetailStackTracer error={error} />
            </>
        );
    }

    if (error instanceof Error) {
        return (
            <>
                {error.message && (
                    <>
                        <h2>
                            Message
                        </h2>

                        <pre className="max-w-7xl mx-auto text-left whitespace-pre-wrap bg-gray-300 p-4">
                            {error.message}
                        </pre>
                    </>
                )}

                {'request' in error && (
                    <>
                        <h2>
                            Request
                        </h2>

                        <pre className="max-w-7xl mx-auto text-left whitespace-pre-wrap bg-gray-300 p-4">
                            {JSON.stringify(error.request, null, 4)}
                        </pre>
                    </>
                )}

                {'response' in error && (
                    <>
                        <h2>
                            Response
                        </h2>

                        <pre className="max-w-7xl mx-auto text-left whitespace-pre-wrap bg-gray-300 p-4">
                            {JSON.stringify(error.response, null, 4)}
                        </pre>
                    </>
                )}

                {error.stack && (
                    <>
                        <h2>
                            Stack
                        </h2>

                        <pre className="max-w-7xl mx-auto text-left whitespace-pre-wrap bg-gray-300 p-4">
                            {error.stack}
                        </pre>
                    </>
                )}
            </>
        );
    }

    console.dir(error);

    return null;
}

function ErrorDetailStackTracer({ error }: { error: TypeError }) {
    const [frames, setFrames] = useState<StackFrame[]>();

    useEffect(() => {
        (async () => {
            const frames = await StackTrace.fromError(error);
            setFrames(frames);
        })();
    }, []);

    if (!frames) {
        return <p>Loading...</p>;
    }

    return (
        <>
            {frames.map((frame, i) => (
                <div key={i}>
                    <h3 className="text-lg mt-2 font-bold">
                        {i + 1}. {frame.functionName}()
                    </h3>
                    {frame.fileName && (
                        <p>
                            {frame.fileName.replace('https://admin.dev.juje.co/dist/js/', '')}:{frame.lineNumber}:{frame.columnNumber}
                        </p>
                    )}
                </div>
            ))}
        </>
    );
}
